import type {
  AssetGroupAssetUpdateManyRequestType,
  AssetGroupCreateOneRequestType,
  AssetGroupGetListRequestType,
  AssetGroupType,
  AssetGroupUpdateOneRequestType,
  AssetGroupWithAssetsType,
} from "../schemas/AssetGroupSchemas.ts";
import type {
  NetworkConfigurationCreateOneRequestType,
  NetworkConfigurationGetListRequestType,
  NetworkConfigurationType,
  NetworkConfigurationUpdateManyRequestType,
} from "../schemas/NetworkSchemas.ts";
import type {
  CreateOneResponseType,
  GetListResponseType,
  GetOneRequestType,
  GetOneResponseType,
  ResourceAdapterType,
  UpdateOneRequestType,
  UpdateOneResponseType,
} from "../util/index.ts";
import { fetchAndCollate, getFilterObject } from "../util/index.ts";
import { restAPI } from "./api.ts";

export interface AssetGroupAdapterType
  extends Pick<
    ResourceAdapterType<AssetGroupType>,
    "getList" | "getOne" | "deleteOne"
  > {
  getList: (
    params: AssetGroupGetListRequestType
  ) => Promise<GetListResponseType<AssetGroupWithAssetsType>>;
  createOne: (
    params: AssetGroupCreateOneRequestType
  ) => Promise<CreateOneResponseType<AssetGroupType>>;
  updateOne: (
    params: AssetGroupUpdateOneRequestType
  ) => Promise<UpdateOneResponseType<AssetGroupType>>;
  migrate: (
    params: UpdateOneRequestType<unknown>
  ) => Promise<GetOneResponseType<AssetGroupType>>;
  configurations: {
    getList: (
      params: NetworkConfigurationGetListRequestType
    ) => Promise<GetListResponseType<NetworkConfigurationType>>;
    updateMany: (
      params: NetworkConfigurationUpdateManyRequestType
    ) => Promise<GetListResponseType<NetworkConfigurationType>>;
    createOne: (
      params: NetworkConfigurationCreateOneRequestType
    ) => Promise<GetListResponseType<NetworkConfigurationType>>;
  };
  assets: {
    updateMany: (
      params: AssetGroupAssetUpdateManyRequestType
    ) => Promise<{ assets: string[] }>;
  };
}

export const AssetGroupAdapter: AssetGroupAdapterType = {
  getList: async ({
    page,
    pageSize,
    sortBy,
    sortDirection,
    groupBy,
    filters,
    meta = {},
  }) =>
    fetchAndCollate(
      ({ page, pageSize }) =>
        restAPI.nodeAPI.POST<GetListResponseType<AssetGroupWithAssetsType>>({
          endpoint: "/asset_groups/search",
          query: meta.period ? { period: meta.period } : undefined,
          body: {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_direction: sortDirection,
            group_by: groupBy,
            filter: getFilterObject(filters),
          },
        }),
      page,
      pageSize
    ),

  getOne: async ({ id, meta }: GetOneRequestType<{ period: string }>) =>
    restAPI.nodeAPI
      .GET<AssetGroupWithAssetsType>({
        endpoint: `/asset_groups/${id}`,
        query: meta?.period ? { period: meta.period } : {},
      })
      .then((resp) => ({ data: resp })),
  /**
   * Create a single Asset Group (otherwise known as a Network)
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/asset_groups/create_asset_group
   */
  createOne: async ({
    data: {
      flow_ids,
      name,
      status,
      custom_attributes,
      reporting_group_id,
      measurement_type,
    },
  }) =>
    restAPI.nodeAPI
      .POST<AssetGroupWithAssetsType>({
        version: 2,
        endpoint: "/asset_groups",
        body: {
          asset_ids: flow_ids ?? [],
          asset_group_type_id: "network",
          name,
          status,
          custom_attributes,
          reporting_group_id,
          measurement_type,
        },
      })
      .then((resp) => ({ data: resp })),
  /**
   * Update a single Asset Group (otherwise known as a Network)
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/asset_groups/put_asset_group
   */
  updateOne: ({
    id,
    data: { reporting_group_id, name, status, custom_attributes, ...rest },
  }) =>
    restAPI.nodeAPI
      .PUT<AssetGroupType>({
        endpoint: `/asset_groups/${id}`,
        body: {
          ...rest,
          name,
          status,
          reporting_group_id,
          custom_attributes,
        },
      })
      .then((resp) => ({ data: resp })),
  /**
   * Delete a single Asset Group (otherwise known as a Network)
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/asset_groups/put_asset_group
   */
  deleteOne: ({ id }) =>
    restAPI.nodeAPI.DELETE({ endpoint: `/asset_groups/${id}` }),
  migrate: ({ id }) =>
    restAPI.nodeAPI.POST({ endpoint: `/networks/${id}/migrate` }),
  configurations: {
    getList: ({ filters, meta }) =>
      fetchAndCollate(({ page, pageSize }) =>
        restAPI.nodeAPI.PUT({
          endpoint: `/networks/${meta?.networkId}/${filters?.yearMonth}`,
          body: {
            page,
            page_size: pageSize,
            reporting_group_id: filters?.reportingGroupId,
          },
        })
      ),
    updateMany: ({ data: { input, yearMonth, reportingGroupId }, meta }) =>
      fetchAndCollate(({ page, pageSize }) =>
        restAPI.nodeAPI.PUT({
          endpoint: `/networks/${meta?.networkId}/${yearMonth}`,
          body: {
            reporting_group_id: reportingGroupId,
            page_size: pageSize,
            page,
            input,
          },
        })
      ),
    createOne: ({ data: { yearMonth, promoteToRecord }, meta }) =>
      restAPI.nodeAPI.POST<GetListResponseType<NetworkConfigurationType>>({
        endpoint: `/networks/${meta?.networkId}/${yearMonth}/run`,
        body: {
          promote_to_record: promoteToRecord,
        },
      }),
  },
  assets: {
    /**
     * Update the assets in a group, and it's corresponding network estimation method
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/asset_groups/update_asset_group_assets
     * @returns the new set of asset_ids
     */
    updateMany: ({ ids: assetIds, meta }) =>
      restAPI.nodeAPI.PUT<{ assets: string[] }>({
        endpoint: `/asset_groups/${meta?.assetGroupId}/asset`,
        body: {
          asset_ids: assetIds,
        },
      }),
  },
};
